// 首頁「事件類別統計 · 近 7 天」（2026-08-20 業主裁決，取代原本的事件時間軸）。
//
// 原本這裡渲染的是與 /events 同一支 `EventTimeline` 元件——連工具列、範圍 chip、無限捲動
// 都一模一樣，也就是使用者 albert 說的「首頁的事件時間軸跟事件頁面重複了」。統計不是清單
// 的縮小版：它回答「這一週各類事件各發生幾次」，清單答不出來（要自己數），而事件頁也不該
// 回答（它是查詢工具）。
//
// 規格（業主 2026-08-20）：
//   - 分群沿用既有的型別判定，不自己發明（見 homeEventStats.ts）。
//   - 排除「系統」群（維運/稽核紀錄），否則「裝置已解除綁定」會佔掉一格。
//   - 固定顯示各群、含計數 0；0 有資訊量，且版面不跳動。
//   - 窗口＝近 7 天，標題明寫。
//   - **不新增 API 呼叫**：沿用首頁本來就要抓的那批事件在 client 端分群計數
//     （後端沒有聚合端點，消費端只有逐機的事件/logbook 分頁）。
//   - 計數單位＝原始事件，不套用時間軸的「相鄰同類合併」。
//   - 點一格 → 事件頁並套用該 cluster 篩選。
//
// ⚠️ 載入中 / 失敗 / 真的沒事，三種狀態必須長得不一樣。把「還沒載完」畫成「各群都 0」
// 是本專案反覆踩過的坑（iOS `HomeOverviewView.securityLoadingCard`、Android
// `HomeEventTimelineInline` 的同段註解都是同型自審紀錄）。

import { ClusterIcon } from '../../components/ClusterIcon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { Illustration } from '../../components/Illustration';
import { clusterInfoOf, fetchDeviceEvents, type EventItem } from '../events/eventsLib';
import { countHomeEventStats, HOME_EVENT_STAT_ORDER } from './homeEventCounts';

/** 事件清單需要的最小裝置形狀（id + 顯示名）。 */
export interface StatsDevice {
  id: string;
  name: string;
}

/** 首頁摘要窗口（天）。與事件頁預設同值，但這裡**不提供放大**——首頁是摘要不是查詢工具。 */
export const HOME_STATS_WINDOW_DAYS = 7;

// 🔴 **翻頁上限三平台必須一致，改一邊要改三邊**（業主 2026-08-21 裁決）：
//   ・web     本檔 `PAGE_LIMIT` / `MAX_PAGES_PER_DEVICE`
//   ・iOS     `apps/home/ios/Sources/Events.swift` 的 `EVENT_PAGE_SIZE` / `EVENT_MAX_PAGES`
//   ・Android `apps/home/android/.../EventFeed.kt` 的 `EVENT_PAGE_SIZE` / `EVENT_MAX_PAGES`
// 原本是 200×10（web）/ 200×6（Android）/ 100×6（iOS）—— 同一個帳號在三個平台看到三個
// 不同的「近 7 天次數」，而畫面都不說。對齊時取 web 這一組（最寬且已在跑）。
const PAGE_LIMIT = 200; // 伺服端 eventLimitMax；用滿它是為了少跑幾趟。
const MAX_PAGES_PER_DEVICE = 10;

/** 逐機翻頁把 7 天窗口撈完。個別裝置失敗不擋其餘（回傳第一個失敗給呼叫端掛橫幅）。 */
async function loadWindow(
  devices: StatsDevice[],
  signal: AbortSignal
): Promise<{ events: EventItem[]; failed: number; firstError: string | null; truncated: boolean }> {
  const nowUs = Date.now() * 1000;
  const fromUs = nowUs - HOME_STATS_WINDOW_DAYS * 86_400_000_000;
  const events: EventItem[] = [];
  let failed = 0;
  let firstError: string | null = null;
  // 至少一台翻到 `MAX_PAGES_PER_DEVICE` 而伺服端還給了 cursor ⇒ 這一段**沒翻完**，
  // 統計出來的每個數字都只是下界。不講的話它與精確值在畫面上長得一模一樣。
  let truncated = false;
  for (const d of devices) {
    try {
      let cursor: string | null = null;
      for (let page = 0; page < MAX_PAGES_PER_DEVICE; page += 1) {
        const res = await fetchDeviceEvents(d.id, { from: fromUs, to: nowUs, cursor, limit: PAGE_LIMIT, signal });
        events.push(...res.items);
        cursor = res.nextCursor;
        if (cursor === null || res.items.length === 0) break;
        if (page === MAX_PAGES_PER_DEVICE - 1) truncated = true;
      }
    } catch (e) {
      if (signal.aborted) throw e;
      failed += 1;
      if (firstError === null) firstError = e instanceof ApiError ? `${e.message}（${e.code}）` : String(e);
    }
  }
  return { events, failed, firstError, truncated };
}

/**
 * `devices === null` = 裝置清單**還沒讀到**（載入中或失敗）。這個區分必須從呼叫端傳進來：
 * 拿「空清單」當初值會讓「還在讀清單」直接畫成「各群都 0」，就是本檔開頭那條禁令。
 */
export function HomeEventStats({
  devices,
  deviceListFailed = false,
}: {
  devices: StatsDevice[] | null;
  /**
   * 裝置清單**讀失敗**（不是還在讀）。沒有這個旗標的話 `devices === null` 會永遠停在載入中
   * —— 自審抓到的：那等於用一個轉不完的圈把失敗藏起來。重試在首頁上方那張錯誤卡
   * （同一顆，不另開第二條路徑）。
   */
  deviceListFailed?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // `null` = 還沒有結果（載入中）。**不要**用「空陣列」當初值——那會讓載入中與「真的沒事件」
  // 長得一模一樣，也就是把載入中講成「各群都 0」。
  const [events, setEvents] = useState<EventItem[] | null>(null);
  /** 這一輪撞到翻頁上限 ⇒ 畫面上的數字是**下界**，格子加 `≥` 並在下面說一句。 */
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const deviceKey = devices === null ? null : devices.map((d) => d.id).join(',');
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setEvents(null);
    setError(null);
    setTruncated(false);
    if (devices === null) return () => ac.abort();   // 清單還沒到 → 維持載入中
    if (devices.length === 0) {
      setEvents([]);
      return () => ac.abort();
    }
    const targets = devices;
    void (async () => {
      try {
        const { events: rows, failed, firstError, truncated: cut } = await loadWindow(targets, ac.signal);
        if (ac.signal.aborted) return;
        setEvents(rows);
        setTruncated(cut);
        // 部分失敗：統計仍照畫（少了那台的數字），但必須講——不講的話畫面看起來完全正常，
        // 而載不到的那台正好可能是出事的那台。
        setError(failed > 0 ? firstError : null);
      } catch (e) {
        if (ac.signal.aborted) return;
        setEvents([]);
        setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.networkError'));
      }
    })();
    return () => ac.abort();
    // deviceKey 而非 devices：清單每輪輪詢都換 identity，用它當依賴會不停重抓。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceKey, reloadKey]);

  const stats = useMemo(() => countHomeEventStats(events ?? []), [events]);

  // ⚠️ **web 目前落不到「帶著分類進事件頁」**，與 iOS/Android 不同。
  // 實查(2026-08-20)：`/events` 掛的是 `recPlayback` 的 `CloudEventTimelineRoute`
  // (App.tsx 的 `<Route path="/events" element={<CloudEventTimelineRoute />} />`)，
  // 那一頁只有「儲存位置(雲端/記憶卡)」與日期兩種篩選，**沒有任何 cluster chip**。
  // 有 cluster chip 的 `features/events/EventsPage` + `features/events/EventTimeline`
  // 是**孤兒**——全 src 只有它們自己的測試檔 import(對照組：`CloudEventTimelineRoute`
  // 在 App.tsx 有實際掛載)。所以「複用事件頁既有的固定 chip」這個前提在 web 不成立，
  // 硬塞一個 `?cluster=` 只會做出一個沒有人讀的參數。
  // 在 web 的事件頁補上 cluster 篩選是另一件事(要動 728 行的回放時間軸元件)，
  // 已回報待裁決；在那之前這裡只帶人到事件頁。
  const open = useCallback(() => navigate('/events'), [navigate]);

  if (deviceListFailed) {
    return (
      <p className="text-secondary" data-testid="home-stats-unavailable">
        {t('home.statsUnavailable')}
      </p>
    );
  }

  // 全部載不到（一台都沒撈成）→ 整區換錯誤 + 重試。有資料時失敗降級成橫幅（見上）。
  if (events !== null && events.length === 0 && error !== null) {
    return (
      <div className="card" role="alert" data-testid="home-stats-error">
        <p className="error-text" style={{ marginTop: 0 }}>{t('home.statsLoadFailed', { error })}</p>
        <button className="btn-ghost" onClick={() => setReloadKey((k) => k + 1)}>
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (events === null) {
    return (
      <p className="text-secondary" data-testid="home-stats-loading">
        {t('home.statsLoading')}
      </p>
    );
  }

  return (
    <div data-testid="home-event-stats">
      {error !== null && (
        <div className="card" role="alert" data-testid="home-stats-partial-error">
          <p className="error-text" style={{ marginTop: 0 }}>{t('home.statsPartialFailed', { error })}</p>
          <button className="btn-ghost" onClick={() => setReloadKey((k) => k + 1)}>
            {t('common.retry')}
          </button>
        </div>
      )}
      <ul className="home-stat-grid">
        {HOME_EVENT_STAT_ORDER.map((key) => {
          // 圖示與名稱沿用事件頁同一套（`events.cluster*`）——同一個群在兩頁必須同名。
          const display = clusterInfoOf(key);
          const label = display.label;
          const n = stats.counts[key];
          return (
            <li key={key}>
              <button
                type="button"
                className="home-stat-cell"
                data-testid={`home-stat-${key}`}
                onClick={open}
                aria-label={
                  truncated && n > 0
                    ? t('home.statsCellLabelLowerBound', { type: label, count: n })
                    : t('home.statsCellLabel', { type: label, count: n })
                }
              >
                <span className="home-stat-icon" aria-hidden>
                  <ClusterIcon kind={key} />
                </span>
                {/* 撞上限時加 `≥`。0 不加：「≥0」沒有資訊，而且被切掉的是較舊的資料，
                    不會讓某一類從 0 變成 0。 */}
                <span className="home-stat-count">{truncated && n > 0 ? `≥${n}` : n}</span>
                <span className="home-stat-label text-secondary">{label}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {/* 撞上限的說明。**與格子上的 `≥` 一起出現，兩者缺一不可**：只給符號多數人讀不出
          「這是下界」；只給句子的話那些數字看起來仍舊精確。 */}
      {truncated && (
        <p className="text-secondary" data-testid="home-stats-truncated">
          {t('home.statsTruncated')}
        </p>
      )}
      {stats.total === 0 && devices !== null && devices.length === 0 && (
        // 一台攝影機都沒有 —— 「沒有事件」在這裡是廢話,要講的是還沒有裝置。
        <div className="card home-stat-empty" data-testid="home-stats-no-devices">
          <p style={{ margin: 0 }}>{t('home.noDevices')}</p>
        </div>
      )}
      {stats.total === 0 && devices !== null && devices.length > 0 && (
        // 真的一筆安全動態都沒有。**只有系統紀錄**時不能寫「沒有事件」——那句在事件頁
        // 打開有一堆列的情況下就是說謊（iOS #6 / Android 既有註解同一件事）。
        <div className="card home-stat-empty" data-testid="home-stats-empty">
          <Illustration name="no-events" alt="" size={120} />
          <p style={{ margin: 0 }}>
            {stats.systemCount > 0
              ? t('home.statsOnlySystem', { count: stats.systemCount, days: HOME_STATS_WINDOW_DAYS })
              : t('home.statsEmpty', { days: HOME_STATS_WINDOW_DAYS })}
          </p>
          <p className="text-secondary" style={{ marginBottom: 0 }}>
            {stats.systemCount > 0 ? t('home.statsOnlySystemHint') : t('home.statsEmptyHint')}
          </p>
        </div>
      )}
    </div>
  );
}
